import * as segmentClient from '@/src/services/segment'
import type { JsonMap, JsonValue } from '@segment/analytics-react-native'
import type { PlaidLinkOnEvent } from 'react-plaid-link'
import type { LinkEventName } from 'react-native-plaid-link-sdk'
import type { userInformationInterface } from '@/src/common/interfaces/user'

export type SegmentComponent =
  | 'Button'
  | 'Dropdown'
  | 'Link'
  | 'Switch'
  | 'Input'
  | 'Profile Button'
  | 'Profile Item'
  | 'Card'
  | 'Card Management Button'
  | 'Reward Option'
  | 'Plaid Link Event'
  | 'DeepLink'
  | 'Debug'
  | 'Main Menu'

export type SegmentAction =
  | 'User Got Started'
  | 'User Logged Out'
  | 'User Pressed Back Button'
  | 'User Inputs First Name'
  | 'User Inputs Last Name'
  | 'User Inputs Phone'
  | 'User Requested OTP'
  | 'User Inputs Email'
  | 'User Inputs OTP'
  | 'Successful user log in'
  | 'User Resent OTP Code'
  | 'User Inputs Address for Google Places Autocomplete'
  | 'User Picks a result for Google Places Autocomplete'
  | 'User Inputs Street Line 1'
  | 'User Inputs Street Line 2'
  | 'User Inputs City'
  | 'User Inputs State'
  | 'User Inputs Zip Code'
  | 'User Creates Account'
  | 'User pressed Profile Button'
  | 'User Pressed the Profile Item My Home'
  | 'User Pressed the Join Waitlist Button for Mesa Card'
  | 'User Pressed the Document Upload Item'
  | 'User Pressed the Settings Item Support'
  | 'User Pressed the Settings Item Legal'
  | 'User Pressed the Settings Item Account'
  | 'User Pressed the Settings Item Refer a Friend'
  | 'User Pressed the Settings Item Feedback'
  | 'User Pressed the Settings Item Website'
  | 'User Pressed the Settings Item Storybook'
  | 'User Consented to Privacy Policy and Terms of Service'
  | 'User Started Mortgage Product V2 Flow'
  | 'User Submitted Mortgage Product V2 Flow'
  | 'User Switched Mortgage Product Details V2 Flow'
  | 'User Pressed the "Continue" button'
  | 'User Pressed the "Card Management" card component'
  | 'User Pressed the "Card Benefits" card component'
  | 'User Pressed the "See All Benefits" link'
  | 'User Pressed the "See All Offers" link'
  | 'User Pressed the "See All Rewards" link'
  | 'User Pressed the "Mortgage Section" card component'
  | 'User Pressed the "Virtual Card" button in the Card Management component'
  | 'User Pressed the "Pay" button in the Card Management component'
  | 'User Pressed the "Accounts" button in the Card Management component'
  | 'User Pressed the "Statements" button in the Card Management component'
  | 'User Pressed the "Get Started" button in the Mortgage Earn section'
  | 'User Opened the Redemption Portal to Book Travel'
  | 'User Opened the Redemption Portal to Gift Cards'
  | 'User Opened the Redemption Portal to History'
  | 'User Opened the Redemption Portal to Statement Credit'
  | 'User Opened Points Transfer to Omni'
  | 'User Enrolled In Benefit'
  | 'User Opened Plaid Integration in order to add an external bank account on the Card Management page'
  | 'User Pressed the "Delete" button to delete an external bank account on the Card Management page'
  | 'User Pressed the "Verify" button to verify an external bank account on the Card Management page'
  | 'User Attempted to verify a card invite code that failed verification'
  | 'User Attempted to verify a card invite code that succeeded to a master invite code'
  | 'User Attempted to verify a card invite code that succeeded to a personal invite code'
  | 'Plaid successfully returned link'
  | 'User Encountered an Error During Plaid Link'
  | 'User tried linking an account that was already linked'
  | 'User is Pending Auth Manual Verification'
  | 'User Pressed the "Cancel Auto Pay" button'
  | 'User Pressed the "Cancel One-time Payment" button'
  | 'User Pressed "Update" Button To Update App'
  | 'User attempted to open a deep link'
  | 'User opened a plaid link'
  | 'User opened an unrecognized link'
  | 'Pressed Share Referral Code Button'
  | 'Pressed Copy Referral Code Button'
  | 'Pressed "Send referrals" button'
  | 'MesaCard Button Apply'
  | 'MesaCard Debug CardContentNotLoaded'
  | 'Pressed the "Update Mortgage Amount" button in the Mortgage Earn section'
  | 'Pressed Share Referral Code Button with Messenger'
  | 'Pressed Share Referral Code Button with WhatsApp'
  | 'Pressed Share Referral Code Button with Reddit'
  | 'Pressed Share Referral Code Button with X'
  | 'Pressed Share Referral Code Button with Messages'
  | 'Pressed Share Referral Code Button with Email'
  | 'Home Tap ReferAFriend'
  | 'Benefits Lowes Enrolls'
  | 'Deferred navigation started'
  | 'Navigating To'
  | 'Pressed Home Screen Card Delivery Module Activate Card Button'
  | 'Benefits Lowes Not_Enrolled'
  | 'Benefits Lowes Enroll_Failed_Error'
  | 'AppsFlyer Deeplink'
  | 'User Pressed "Update" Notification Settings'
  | 'User Pressed the "Notification Settings" Item Account'
  | 'MesaHome MortgagePayment OpenUpdateDialog'
  | 'MesaHome MortgagePayment SubmitUpdatedMonthlyHousingPayment'
  | 'MesaHome MortgagePayment SubmitUpdatedMonthlyHousingPaymentNotEnrolled'
  | 'MesaHome MortgagePayment SelectDocumentToUpload'
  | 'MesaHome MortgagePayment UnselectDocument'
  | 'MesaHome MortgagePayment AgreeToTermsAndConditions'
  | 'MesaHome MortgagePayment SubmitDocuments'

export async function triggerSegmentAction(
  options: JsonMap & {
    component: SegmentComponent
    action: SegmentAction
  },
) {
  await segmentClient.track(options.action, options)
}

type WebPlaidLinkEvent = Parameters<PlaidLinkOnEvent>[0]

export async function triggerSegmentPlaidLinkEvent({
  component,
  action,
  metadata,
}: {
  component: SegmentComponent
  action: LinkEventName | WebPlaidLinkEvent
  metadata?: JsonValue
}) {
  await segmentClient.track(action, { component, action, metadata })
}

export async function triggerSegmentIdentify({
  userId,
  userInformation,
}: {
  userId?: string
  userInformation?: userInformationInterface
}) {
  if (userId) {
    await segmentClient.alias(userId)

    if (userInformation?.email) {
      // if user has already input their personal info
      await segmentClient.identify(userId, {
        id: userInformation.id,
        firstName: userInformation.firstName ?? undefined,
        lastName: userInformation.lastName ?? undefined,
        email: userInformation.email,
        phone: userInformation.phoneNumber ?? undefined,
        username: userInformation.nickname ?? undefined,
        sourcePartnerName: userInformation.sourcePartnerName,
      })
    } else {
      await segmentClient.identify(userId)
    }
  }
}

export async function resetSegmentUser() {
  await segmentClient.reset()
}

void (async function keepSegmentUserInSyncWithMesaUser() {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { useUserStore } = await import('@/src/common/store/useUserStore')
  useUserStore.subscribe(
    (state) => state.userInformation,
    (userInformation) =>
      void triggerSegmentIdentify({ userId: userInformation?.id, userInformation }),
    { fireImmediately: true },
  )
})()

type MobileSegment = typeof import('./index')
type WebSegment = typeof import('./index.web')

true satisfies MobileSegment extends WebSegment
  ? WebSegment extends MobileSegment
    ? true
    : false
  : false
